import React from "react";

import { Badge } from "~/components/ui/badge";
import { Progress } from "~/components/ui/progress";
import { Separator } from "~/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { formatDate } from "~/utils/helpers";

interface PaymentDetailsDrawerProps {
  paymentId: string | null;
  open: boolean;
  onClose: () => void;
  payment: any;
}

const calculateDaysLate = (dueDateString: string | null | undefined) => {
  if (!dueDateString) return 0;
  const dueDate = new Date(dueDateString);
  dueDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = today.getTime() - dueDate.getTime();
  const daysLate = Math.floor(diff / (1000 * 60 * 60 * 24));
  return daysLate > 0 ? daysLate : 0;
};

const PaymentDetailsDrawer: React.FC<PaymentDetailsDrawerProps> = ({
  open,
  onClose,
  payment,
}) => {
  if (!payment) return null;
  const totalAmount = Number(payment.total_amount);
  const paidAmount = Number(payment.paid_amount || 0);
  const proportionalInterest = Number(payment.proportional_interest || 0);
  const progressPercentage =
    totalAmount > 0 ? Math.min(100, (paidAmount / totalAmount) * 100) : 0;
  const remainingAmount =
    payment.remaining_amount || Math.max(0, totalAmount - paidAmount);
  const totalDueWithInterest = remainingAmount + proportionalInterest;
  const daysLate =
    payment.daysLate !== undefined
      ? payment.daysLate
      : calculateDaysLate(payment.payment_due_date);

  const isExpired = daysLate > 0;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Detalles del Pago</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div>
            <h3 className="mb-2 text-sm font-medium text-muted-foreground">
              Cliente
            </h3>
            <p className="text-base font-semibold">{payment.client?.name}</p>
            {payment.client?.tax_id && (
              <p className="text-sm text-muted-foreground">
                RUT: {payment.client.tax_id}
              </p>
            )}
          </div>

          <Separator />

          <div>
            <h3 className="mb-2 text-sm font-medium text-muted-foreground">
              Información de la Solicitud
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="text-muted-foreground">ID de Solicitud</p>
                <p className="font-medium">{payment.id.substring(0, 6)}...</p>
              </div>
              <div>
                <p className="text-muted-foreground">Fecha de Emisión</p>
                <p className="font-medium">{formatDate(payment.created_at)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Fecha de Vencimiento</p>
                <p className="font-medium">
                  {formatDate(payment.payment_due_date)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">
                  {isExpired ? "Días de Atraso" : "Días Restantes"}
                </p>
                {isExpired ? (
                  <span className="inline-block rounded bg-red-500 px-2 py-1 font-semibold text-white">
                    {daysLate} día{daysLate !== 1 ? "s" : ""}
                  </span>
                ) : (
                  <span className="font-medium text-amber-600">
                    {payment.daysRemaining} días
                  </span>
                )}
              </div>
              {isExpired && (
                <div className="col-span-2 mt-1">
                  <p className="text-xs text-gray-600">
                    <span className="font-semibold">Cálculo:</span>{" "}
                    {`Hoy (${formatDate(new Date().toISOString())}) - Fecha de Vencimiento (${formatDate(payment.payment_due_date)}) = `}
                    <span className="font-semibold">
                      {daysLate} día{daysLate !== 1 ? "s" : ""}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>

          <Separator />

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">
                Progreso de Pago
              </h3>
              <Badge
                variant={progressPercentage >= 100 ? "secondary" : "outline"}
                className={
                  progressPercentage >= 100
                    ? "bg-green-500 text-white hover:bg-green-600"
                    : ""
                }
              >
                {progressPercentage.toFixed(0)}% Pagado
              </Badge>
            </div>
            <Progress value={progressPercentage} className="mb-4 h-2" />
            <div className="flex justify-between text-sm">
              <div>
                <p className="text-muted-foreground">Total Pagado</p>
                <p className="font-medium">${paidAmount.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Monto Pendiente</p>
                <p className="font-medium">
                  {remainingAmount.toLocaleString()}{" "}
                  {proportionalInterest > 0 && (
                    <span className="text-xs text-amber-600">
                      (sin interés)
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Interés Proporcional</p>
                <p className="font-medium">
                  {proportionalInterest
                    ? `$${proportionalInterest.toLocaleString()}`
                    : "$0"}
                </p>
              </div>
              <div>
                <p className="font-semibold text-black text-muted-foreground">
                  Total con Interés
                </p>
                <p className="font-bold text-red-500">
                  {totalDueWithInterest > 0
                    ? `$${totalDueWithInterest.toLocaleString()}`
                    : "$0"}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Monto Total</p>
                <p className="font-medium">${totalAmount.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {payment.payments && payment.payments.length > 0 ? (
            <>
              <Separator />
              <div>
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                  Historial de Pagos
                </h3>
                <div className="max-h-60 space-y-3 overflow-y-auto p-1">
                  {payment.payments.map((p, idx) => (
                    <div key={idx} className="rounded-md bg-muted p-3">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">
                          Pago #{idx + 1}
                        </span>
                        <span className="text-sm font-semibold">
                          ${Number(p.amount).toLocaleString()}
                        </span>
                      </div>
                      {p.created_at && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Fecha: {formatDate(p.created_at)}
                        </p>
                      )}
                      {p.payment_method && (
                        <p className="text-xs text-muted-foreground">
                          Método: {p.payment_method}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <Separator />
              <div className="py-4 text-center text-sm text-muted-foreground">
                No se han registrado pagos para esta solicitud
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PaymentDetailsDrawer;
